import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { SubscriptionTier, TokenBalanceResponse, TokenSpendResult } from '../types/tokens';
import { fetchTokenBalance, claimDailyStreak, spendTokens } from '../services/tokenService';

interface TokenContextType {
  balance: number;
  tier: SubscriptionTier;
  isPro: boolean;
  canClaimStreak: boolean;
  streakCountdownMs: number;
  isPurchaseModalOpen: boolean;
  defaultModalTab: 'packs' | 'pro';
  refreshBalance: () => Promise<void>;
  claimStreak: () => Promise<boolean>;
  spend: (action: string, amount?: number, refId?: string, desc?: string) => Promise<TokenSpendResult>;
  openPurchaseModal: (tab?: 'packs' | 'pro') => void;
  closePurchaseModal: () => void;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export const TokenProvider: React.FC<{ children: React.ReactNode; userName?: string }> = ({
  children,
  userName
}) => {
  const [balance, setBalance] = useState<number>(5);
  const [tier, setTier] = useState<SubscriptionTier>('free');
  const [isPro, setIsPro] = useState<boolean>(false);
  const [canClaimStreak, setCanClaimStreak] = useState<boolean>(true);
  const [streakCountdownMs, setStreakCountdownMs] = useState<number>(0);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState<boolean>(false);
  const [defaultModalTab, setDefaultModalTab] = useState<'packs' | 'pro'>('packs');

  // Strict concurrency mutex on network queries (Rule 1)
  const isFetchingRef = useRef<boolean>(false);

  const refreshBalance = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const data: TokenBalanceResponse | null = await fetchTokenBalance();
      if (data && data.success) {
        setBalance(data.balance ?? 5);
        setTier(data.subscriptionTier || 'free');
        setIsPro(Boolean(data.isPro));
        setCanClaimStreak(Boolean(data.canClaimStreak));
        setStreakCountdownMs(data.streakCountdownMs || 0);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Sync on login / user changes
  useEffect(() => {
    refreshBalance();
  }, [userName, refreshBalance]);

  // Listen to cross-component balance update events
  useEffect(() => {
    const handleUpdate = () => {
      refreshBalance();
    };
    window.addEventListener('soulsync:balance_updated', handleUpdate);
    return () => window.removeEventListener('soulsync:balance_updated', handleUpdate);
  }, [refreshBalance]);

  // Background refresh cadence adhering to Rule 2 (every 45 seconds, strictly >= 2.5s)
  useEffect(() => {
    const interval = setInterval(() => {
      refreshBalance();
    }, 45000);
    return () => clearInterval(interval);
  }, [refreshBalance]);

  const claimStreak = async (): Promise<boolean> => {
    const res = await claimDailyStreak();
    if (res.success) {
      if (res.newBalance !== undefined) setBalance(res.newBalance);
      setCanClaimStreak(false);
      await refreshBalance();
      return true;
    }
    return false;
  };

  const spend = async (
    action: string,
    amount = 1,
    refId?: string,
    desc?: string
  ): Promise<TokenSpendResult> => {
    const res = await spendTokens(action, amount, refId, desc);
    if (res.success && res.newBalance !== undefined) {
      setBalance(res.newBalance);
    }
    return res;
  };

  const openPurchaseModal = (tab: 'packs' | 'pro' = 'packs') => {
    setDefaultModalTab(tab);
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
  };

  return (
    <TokenContext.Provider
      value={{
        balance,
        tier,
        isPro,
        canClaimStreak,
        streakCountdownMs,
        isPurchaseModalOpen,
        defaultModalTab,
        refreshBalance,
        claimStreak,
        spend,
        openPurchaseModal,
        closePurchaseModal
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export function useTokens(): TokenContextType {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useTokens must be used within a TokenProvider');
  }
  return context;
}
